import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Wrench, Plus, Trash2, CheckCircle2, AlertTriangle, Calendar, X } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'service', label: 'Service' },
  { value: 'besiktning', label: 'Besiktning' },
  { value: 'certifikat', label: 'Certifikat' },
  { value: 'försäkring', label: 'Försäkring' },
  { value: 'annat', label: 'Annat' },
];

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / (1000 * 60 * 60 * 24));
};

const statusFor = (record) => {
  if (record.completed_at) return { label: 'Klart', color: 'green' };
  const d = daysUntil(record.due_date);
  if (d === null) return { label: 'Okänt', color: 'gray' };
  if (d < 0) return { label: `${Math.abs(d)} dagar försenat`, color: 'red' };
  if (d === 0) return { label: 'Idag!', color: 'red' };
  if (d <= 7) return { label: `Om ${d} dagar`, color: 'amber' };
  if (d <= 30) return { label: `Om ${d} dagar`, color: 'orange' };
  return { label: `Om ${d} dagar`, color: 'blue' };
};

const colorClasses = {
  red: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900',
  amber: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900',
  orange: 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-900',
  blue: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900',
  green: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900',
  gray: 'bg-muted/50 text-muted-foreground border-border',
};

const MachineServiceDialog = ({ machine, open, onClose, onChanged }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type: 'service', title: '', due_date: '', notes: '' });
  const [completeFor, setCompleteFor] = useState(null);
  const [nextDate, setNextDate] = useState('');

  const fetchRecords = async () => {
    if (!machine?.id) return;
    setLoading(true);
    try {
      const res = await api.listMachineService(machine.id);
      setRecords(res.data);
    } catch {
      toast.error('Kunde inte hämta service-poster');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchRecords(); /* eslint-disable-next-line */ }, [open, machine?.id]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title || !form.due_date) { toast.error('Titel och datum krävs'); return; }
    try {
      await api.addMachineService(machine.id, form);
      toast.success('Service-post tillagd. AI påminner inför förfallodagen.');
      setForm({ type: 'service', title: '', due_date: '', notes: '' });
      fetchRecords(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Misslyckades'); }
  };

  const handleDelete = async (rid) => {
    if (!window.confirm('Ta bort service-posten?')) return;
    try {
      await api.deleteMachineService(machine.id, rid);
      toast.success('Borttagen'); fetchRecords(); onChanged?.();
    } catch { toast.error('Misslyckades'); }
  };

  const handleComplete = async () => {
    if (!completeFor) return;
    try {
      await api.completeMachineService(machine.id, completeFor.id, nextDate ? { next_due_date: nextDate } : {});
      toast.success(nextDate ? 'Markerad som klar — nästa påminnelse satt' : 'Markerad som klar');
      setCompleteFor(null); setNextDate(''); fetchRecords(); onChanged?.();
    } catch { toast.error('Misslyckades'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="machine-service-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench size={18} className="text-[#E8613A]" /> Service & besiktningar — {machine?.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleAdd} className="bg-muted/30 border border-border rounded-lg p-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A]">Lägg till påminnelse</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Typ</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full h-9 px-3 rounded-md border border-border bg-background text-sm" data-testid="srv-type">
                {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Förfallodatum</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="srv-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Titel</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="T.ex. Årsbesiktning grävmaskin" className="mt-1" data-testid="srv-title" />
          </div>
          <div>
            <Label className="text-xs">Kommentar (valfritt)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Verkstad / kontaktperson / kostnad" className="mt-1" data-testid="srv-notes" />
          </div>
          <Button type="submit" className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="srv-add">
            <Plus size={14} className="mr-1.5" /> Lägg till
          </Button>
          <p className="text-[11px] text-muted-foreground -mt-1">
            🔔 AI påminner 30, 10, 7, 3, 2, 1 dag innan + på dagen via push-notis till admin.
          </p>
        </form>

        <div className="mt-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Befintliga poster</p>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Laddar...</p>
          ) : records.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Inga service-poster ännu.</p>
          ) : records.map(r => {
            const st = statusFor(r);
            return (
              <div key={r.id} className={`border rounded-lg p-3 ${colorClasses[st.color]}`} data-testid={`record-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {st.color === 'red' && !r.completed_at && <AlertTriangle size={12} />}
                      {r.completed_at && <CheckCircle2 size={12} />}
                      <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">{r.type}</span>
                      <span className="text-[10px] opacity-60">·</span>
                      <span className="text-[10px] font-medium">{st.label}</span>
                    </div>
                    <p className="text-sm font-bold mt-1">{r.title}</p>
                    <p className="text-[11px] opacity-80 flex items-center gap-1 mt-0.5"><Calendar size={10} /> {r.due_date}</p>
                    {r.notes && <p className="text-[11px] opacity-70 mt-1">{r.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    {!r.completed_at && (
                      <button onClick={() => setCompleteFor(r)} className="p-1.5 rounded hover:bg-black/10" title="Markera klart" data-testid={`complete-${r.id}`}>
                        <CheckCircle2 size={14} />
                      </button>
                    )}
                    <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded hover:bg-black/10" title="Ta bort" data-testid={`del-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Complete sub-dialog */}
        {completeFor && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={() => setCompleteFor(null)}>
            <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="complete-dialog">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold">Markera klart</h3>
                <button onClick={() => setCompleteFor(null)}><X size={16} /></button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Bra! Vill du sätta ett nytt påminnelsedatum direkt (t.ex. nästa årsservice)?</p>
              <Label className="text-xs">Nästa förfallodatum (valfritt)</Label>
              <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="mt-1 mb-4" data-testid="next-date" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCompleteFor(null)} className="flex-1">Avbryt</Button>
                <Button onClick={handleComplete} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-complete">
                  {nextDate ? 'Klar + ny' : 'Klar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MachineServiceDialog;
